import { useAuth } from './AuthContext';
import { usePermissions } from './usePermissions';

export type SystemRole = 'PLATFORM_ADMIN' | 'ADMIN' | 'USER' | 'READONLY';

export interface UseAuthorizationResult {
  /** Globale System-Admin-Rolle (PLATFORM_ADMIN) */
  isPlatformAdmin: boolean;
  /** PLATFORM_ADMIN oder ADMIN */
  isAdmin: boolean;
  /** User hat mindestens eine der angegebenen System-Rollen */
  hasRole: (...roles: SystemRole[]) => boolean;
  /** User hat die Permission oder ist PLATFORM_ADMIN */
  can: (permission: string) => boolean;
  /** User hat mindestens eine der Permissions oder ist PLATFORM_ADMIN */
  canAny: (...permissions: string[]) => boolean;
  /** User darf Modul sehen (ctb.view / ctb.edit / ctb.admin oder PLATFORM_ADMIN) */
  canViewModule: (module: string) => boolean;
  /** User hat Admin-Rechte auf ein Modul (ctb.admin oder PLATFORM_ADMIN) */
  canAdminModule: (module: string) => boolean;
  isLoading: boolean;
}

export function useAuthorization(): UseAuthorizationResult {
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, isLoading } = usePermissions();

  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN';
  const isAdmin = isPlatformAdmin || user?.role === 'ADMIN';

  const hasRole = (...roles: SystemRole[]): boolean =>
    !!user && roles.includes(user.role as SystemRole);

  const can = (permission: string): boolean =>
    isPlatformAdmin || hasPermission(permission);

  const canAny = (...permissions: string[]): boolean =>
    isPlatformAdmin || hasAnyPermission(permissions);

  const canViewModule = (module: string): boolean =>
    isPlatformAdmin ||
    hasAnyPermission([`${module}.view`, `${module}.edit`, `${module}.admin`]);

  const canAdminModule = (module: string): boolean =>
    isPlatformAdmin || hasPermission(`${module}.admin`);

  return {
    isPlatformAdmin,
    isAdmin,
    hasRole,
    can,
    canAny,
    canViewModule,
    canAdminModule,
    isLoading,
  };
}
